import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../context/auth';
import * as T from '../../components/Typography';

const ViewReport: React.FC = () => {
  const { salesforceContactId } = useParams<{ salesforceContactId: string }>();
  const [searchParams] = useSearchParams();
  const { user, token: contextToken } = useAuth();

  // Get token from URL params or auth context
  const token = searchParams.get('token') || contextToken;
  const type = searchParams.get('type');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const pdfUrlRef = useRef<string | null>(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchReport = async () => {
      // Prevent duplicate requests
      if (hasFetched.current) {
        return;
      }

      if (!salesforceContactId || !token) {
        if (!token) {
          console.error('No token available yet, skipping fetch');
          return;
        }
        setError('Missing required parameters');
        setIsLoading(false);
        return;
      }

      if (!type) {
        setError('No type provided');
        setIsLoading(false);
        return;
      }

      hasFetched.current = true;

      // Check if user is authorized to view this report
      if (user?.salesforceContactId !== salesforceContactId) {
        setError('Unauthorized access');
        setIsLoading(false);
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL;
      const reportUrl = `${apiUrl}/survey/${salesforceContactId}/download-report?type=${type}`;

      console.log('Making request to:', reportUrl);
      console.log('Using token:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');

      const makeRequest = async (authToken: string | null) => {
        const response = await fetch(reportUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.text();
          let errorMessage;

          try {
            const parsedError = JSON.parse(errorData);
            errorMessage = parsedError.message;
          } catch {
            errorMessage = errorData;
          }

          const error = new Error(errorMessage) as any;
          error.status = response.status;
          error.message = errorMessage;
          error.response = { status: response.status, data: { message: errorMessage } };
          throw error;
        }

        return response;
      };

      try {
        let response = await makeRequest(token);

        console.log('Response status:', response.status, response.statusText);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
        pdfUrlRef.current = url;
        setIsLoading(false);
      } catch (err: any) {
        console.error('Error fetching report:', err);

        // Handle specific error cases
        if (err?.status === 403) {
          if ((err?.message === 'Invalid token' || err?.message === 'Unauthorized') && token) {
            // Try to refresh the token once
            try {
              const apiUrl = import.meta.env.VITE_API_URL || '/api';
              const refreshResponse = await fetch(`${apiUrl}/auth/refresh`, {
                method: 'POST',
                credentials: 'include'
              });

              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json();
                const newToken = refreshData.accessToken;

                // Update localStorage with new token
                localStorage.setItem('accessToken', newToken);

                // Retry the request with the new token
                const retryResponse = await makeRequest(newToken);
                const retryBlob = await retryResponse.blob();
                const retryUrl = window.URL.createObjectURL(retryBlob);
                setPdfUrl(retryUrl);
                pdfUrlRef.current = retryUrl;
                setIsLoading(false);
                return; // Success, exit the function
              } else {
                throw new Error('Token refresh failed');
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
              setError('Your session has expired. Please refresh the page to continue.');
            }
          } else {
            setError('You are not authorized to view this report.');
          }
        } else if (err?.status === 404) {
          setError('Report not found. Please check the URL and try again.');
        } else if (err?.status >= 500) {
          setError('Server error. Please try again later.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load report');
        }

        setIsLoading(false);
      }
    };

    fetchReport();


    // Cleanup function to revoke the blob URL when component unmounts
    return () => {
      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current);
      }
    };
  }, [salesforceContactId, token, user?.salesforceContactId, type]);


  if (isLoading) {
    return (
      <Box
        width="100vw"
        height="100vh"
        margin={0}
        padding={0}
        position="fixed"
        top={0}
        left={0}
        zIndex={9999}
        bgcolor="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Loading your report...</Typography>
      </Box>
    );
  }

  if (error || !type) {
    return (
      <Box
        width="100vw"
        height="100vh"
        margin={0}
        padding={0}
        position="fixed"
        top={0}
        left={0}
        zIndex={9999}
        bgcolor="white"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={3}
        p={3}
      >
        <T.H4 color="red">Error Loading Report</T.H4>
        <T.TextSmallRegular color="gray" textAlign="center">
          {error || 'No type provided'}
        </T.TextSmallRegular>
      </Box>
    );
  }

  return (
    <Box
      width="100vw"
      height="100vh"
      margin={0}
      padding={0}
      position="fixed"
      top={0}
      left={0}
      zIndex={9999}
      bgcolor="white"
    >
      {pdfUrl && (
        <Box
          component="iframe"
          src={pdfUrl}
          width="100%"
          height="100%"
          border="none"
          title="Feedback Report"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        />
      )}
    </Box>
  );
};

export default ViewReport;
